// useSettingsPlanPaneViewModel.js

import { useMemo } from 'react';

// Utils
import {
    formatBillingStatus,
    formatBytes,
    formatDateTime,
    formatMonthLabel,
    formatPlanPrice,
    formatUsageValue,
    formatWholeCount,
    secondsToWholeMinutes,
} from '../utils/settingsPlanPaneUtils';

/**
 * useSettingsPlanPaneViewModel
 *
 * Builds the display-oriented summary state for SettingsPlanPane.
 *
 * @param {object} params - Hook parameters.
 * @param {object} params.apiLimits - Workspace API usage limits snapshot.
 * @param {object} params.billingRecord - Workspace billing record.
 * @param {boolean} params.billingLoading - Whether the billing record is loading.
 * @param {object} params.currentPlan - Active workspace plan row.
 * @param {boolean} params.isCurrentPlanHiddenFromCatalog - Whether the active plan is hidden from the public catalog.
 * @param {object} params.liveUsage - Workspace live usage snapshot.
 * @param {object} params.workspace - Active workspace model.
 *
 * @returns {object} Derived plan summary and usage view state.
 */
const useSettingsPlanPaneViewModel = ({
    apiLimits,
    billingLoading,
    billingRecord,
    currentPlan,
    isCurrentPlanHiddenFromCatalog,
    liveUsage,
    workspace,
}) => {
    const currentPlanPriceText = useMemo(() => {
        return formatPlanPrice(currentPlan);
    }, [currentPlan]);

    const billingStatusText = useMemo(() => {
        return billingLoading ? 'Loading...' : formatBillingStatus(billingRecord?.billingStatus);
    }, [billingLoading, billingRecord?.billingStatus]);

    const billingRenewsText = useMemo(() => {
        return billingLoading ? 'Loading...' : formatDateTime(billingRecord?.currentPeriodEnd);
    }, [billingLoading, billingRecord?.currentPeriodEnd]);

    const membersUsageText = useMemo(() => {
        const workspaceMemberCount = Math.max(0, Math.floor(Number(workspace?.memberCount || 0)));
        const currentPlanUserLimit = Number(currentPlan?.maxUsers || 0);

        if (currentPlanUserLimit > 0) {
            return `${formatWholeCount(workspaceMemberCount)} / ${formatWholeCount(currentPlanUserLimit)}`;
        }

        return `${formatWholeCount(workspaceMemberCount)} active`;
    }, [currentPlan?.maxUsers, workspace?.memberCount]);

    const storageUsageState = useMemo(() => {
        const pooledStorageBytes = Math.max(0, Math.floor(Number(currentPlan?.pooledStorageBytes || 0)));
        const storageBytesUsed = Math.max(0, Math.floor(Number(workspace?.storageBytesUsed || 0)));
        const storageBytesReserved = Math.max(0, Math.floor(Number(workspace?.storageBytesReserved || 0)));
        const storageBytesEffective = storageBytesUsed + storageBytesReserved;

        const storageUsageText = pooledStorageBytes > 0
            ? `${formatBytes(storageBytesEffective)} / ${formatBytes(pooledStorageBytes)}`
            : `${formatBytes(storageBytesEffective)} used`;

        const storageReservedNote = storageBytesReserved > 0
            ? `${formatBytes(storageBytesReserved)} is currently reserved for in-flight uploads.`
            : '';

        return {
            storageReservedNote,
            storageUsageText,
        };
    }, [currentPlan?.pooledStorageBytes, workspace?.storageBytesReserved, workspace?.storageBytesUsed]);

    const shouldShowApiUsage = Boolean(currentPlan?.apiEnabled || isCurrentPlanHiddenFromCatalog);
    const shouldShowLiveUsage = Boolean(currentPlan?.liveEnabled || isCurrentPlanHiddenFromCatalog);
    const shouldShowRecordingUsage = Boolean(
        (currentPlan?.liveEnabled && currentPlan?.liveRecordingEnabled)
        || isCurrentPlanHiddenFromCatalog
    );

    const apiUsageSummary = useMemo(() => {
        const monthlyRuns = apiLimits?.usage?.apiMonthlyRuns || null;
        const requestCount = Math.max(0, Math.floor(Number(monthlyRuns?.requestCount || 0)));
        const readCount = Math.max(0, Math.floor(Number(monthlyRuns?.readCount || 0)));
        const writeCount = Math.max(0, Math.floor(Number(monthlyRuns?.writeCount || 0)));
        const limit = Math.max(0, Math.floor(Number(monthlyRuns?.limit || currentPlan?.apiMonthlyRunLimit || 0)));

        return {
            detailText: `${formatWholeCount(readCount)} reads • ${formatWholeCount(writeCount)} writes`,
            limit,
            monthLabel: formatMonthLabel(monthlyRuns?.monthKey),
            requestCount,
            summaryText: formatUsageValue({
                limit,
                used: requestCount,
            }),
        };
    }, [apiLimits, currentPlan?.apiMonthlyRunLimit]);

    const liveUsageSummary = useMemo(() => {
        const participantMinutesUsed = secondsToWholeMinutes(liveUsage?.participantSeconds || 0);
        const recordingMinutesUsed = secondsToWholeMinutes(liveUsage?.egressSeconds || 0);
        const participantMinutesCap = Math.max(0, Math.floor(Number(currentPlan?.liveParticipantMinutesMonthlyCap || 0)));
        const recordingMinutesCap = Math.max(0, Math.floor(Number(currentPlan?.liveEgressMinutesMonthlyCap || 0)));
        const activeRoomCount = Math.max(0, Math.floor(Number(liveUsage?.activeRoomCount || 0)));
        const activeParticipantCount = Math.max(0, Math.floor(Number(liveUsage?.activeParticipantCount || 0)));

        return {
            activeNowText: `${formatWholeCount(activeRoomCount)} room${activeRoomCount === 1 ? '' : 's'} • ${formatWholeCount(activeParticipantCount)} participants`,
            monthLabel: formatMonthLabel(liveUsage?.monthKey),
            participantMinutesText: formatUsageValue({
                limit: participantMinutesCap,
                used: participantMinutesUsed,
            }),
            recordingMinutesText: formatUsageValue({
                limit: recordingMinutesCap,
                used: recordingMinutesUsed,
            }),
        };
    }, [currentPlan?.liveEgressMinutesMonthlyCap, currentPlan?.liveParticipantMinutesMonthlyCap, liveUsage]);

    return {
        currentPlanPriceText,
        billingStatusText,
        billingRenewsText,
        membersUsageText,
        storageReservedNote: storageUsageState.storageReservedNote,
        storageUsageText: storageUsageState.storageUsageText,
        shouldShowApiUsage,
        shouldShowLiveUsage,
        shouldShowRecordingUsage,
        apiUsageSummary,
        liveUsageSummary,
    };
};

export default useSettingsPlanPaneViewModel;